<template>
    <div class="top-bar">
        <div class="top-bar__logos">
            <router-link :to="{ name: 'flow-dashboard' }">
                <icon-back />
            </router-link>
            <logo />
        </div>

        <div class="top-bar__info">
            <input-group v-model="brand">
                <template #input>
                    <flex-container
                        nowrap
                        align-items="center"
                        :gap="Size.xs"
                    >
                        <text-decorator :size="FontSize.sm" bold>
                            Merk /
                        </text-decorator>
                        <select-input
                            variant="inline"
                            :items="brandCollection.data.value"
                            value-key-name="id"
                            label-key-name="name"
                        />
                    </flex-container>
                </template>
            </input-group>

            <input-group v-model="teams">
                <template #input>
                    <flex-container
                        nowrap
                        align-items="center"
                        :gap="Size.xs"
                    >
                        <text-decorator :size="FontSize.sm" bold>
                            Vestiging /
                        </text-decorator>
                        <multi-select-input
                            variant="inline"
                            :items="teamCollection.data.value"
                            value-key-name="id"
                            label-key-name="name"
                        />
                    </flex-container>
                </template>
            </input-group>

            <inline-edit-text
                v-model="flowName"
            />
        </div>

        <div class="top-bar__actions">
            <input-group v-model="flowActive">
                <template #input>
                    <switch-input />
                    <text-decorator :size="FontSize.xs">
                        {{ flowActive ? 'Actief' : 'Inactief' }}
                    </text-decorator>
                </template>
            </input-group>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {
        Size,
        FontSize,
        InputGroup,
        SelectInput,
        MultiSelectInput,
        FlexContainer,
        SwitchInput,
        TextDecorator,
    } from '@keyvue/one-ui';

    import { onMounted, ref, watch } from 'vue';
    import IconBack from '@keyvue/one-awesome-icons/icons/regular/arrow-left-long.svg?component';
    import Logo from '@/assets/icons/logo.svg?component';
    import InlineEditText from '@/flow/flow/components/InlineEditText.vue';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import { Brand, Team } from '@keyvue/one-api-client';

    interface Props {
        flowActive: boolean;
        flowName: string;
        brand: Brand,
        teams: Team[],
    }

    const { getLazyResourceCollection, getLazySubResourceCollection, currentCompany } = useClient();
    const props = defineProps<Props>();
    const emit = defineEmits<{
        (e: 'flow-active-changed', value: boolean): void;
        (e: 'flow-name-changed', value: string): void;
        (e: 'brand-changed', value: Brand): void;
        (e: 'teams-changed', value: Team[]): void;
    }>();

    const flowName = ref(props.flowName);
    const flowActive = ref(props.flowActive);
    const brand = ref(props.brand);
    const teams = ref(props.teams);

    const brandCollection = getLazyResourceCollection(Brand);
    const teamCollection = getLazySubResourceCollection(currentCompany.value, Team, 'teams', {
        'active': 'true',
        'order[name]': 'asc',
    });

    async function loadData() {
        await Promise.all([
            brandCollection.load(),
            teamCollection.load(),
        ]);
    }

    watch(() => props.flowName, (newValue) => {
        flowName.value = newValue;
    });

    watch(() => props.flowActive, (newValue) => {
        flowActive.value = newValue;
    });

    watch(() => flowName.value, (newValue) => emit('flow-name-changed', newValue));
    watch(() => flowActive.value, (newValue) => emit('flow-active-changed', newValue));

    onMounted(() => {
        loadData();
    });
</script>

<style scoped lang="scss">
.top-bar {
    background-color: white;
    border-bottom: 1px solid color(gray-300);
    display: grid;
    grid-template-columns: min-content 1fr min-content;
    align-items: center;
    justify-content: space-between;
    padding: size(md);
    gap: size(xl);

    &__logos {
        display: flex;
        align-items: center;
        gap: size(md);

        svg:first-child {
            height: 2rem;
            border-radius: 50%;
            transition-property: background-color, outline;
            transition-duration: .2s;
            transition-timing-function: ease-in-out;

            &:hover {
                background-color: color(gray-200);
                outline: color(gray-200) solid size(sm);
            }
        }
    }

    &__info {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        justify-content: space-evenly;
        align-items: center;
    }

    &__actions {
        border-left: 1px solid color(gray-300);
        padding-left: size(md);

        display: flex;
        gap: size(md);

        .button-block {
            min-width: 100px; /* Fix button being too small */
        }
    }

    svg {
        height: 48px;
        color: var(--theme-colors-default--primary);
    }
}
</style>
